import { DEFAULT_AGENCY_PAGE_LIST_SIZE, fetchAgencies } from '@/api/customerMetaApi';
import { getAgencySortByOption } from '@/helpers/AgencyDirectory/agencyRouteParams';
import {
  applySelectedLocationsToRoute,
  getLocationGeoId,
  haveSameSearchedGeoIds,
  resolveGeoLocations,
} from '@/stores/geoLocationHelpers';
import { LoadState } from '@/stores/types';
import { ListingType, SortingOption, type Agency } from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, Locale } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';
import type { LocationQuery } from 'vue-router';

import {
  AGENCY_DIRECTORY_STASH_QUERY_KEYS,
  pickDirectoryStashQueryFromRoute,
  type AgencyDirectoryStashQuery,
} from '@/helpers/Directory/directoryToggleRouteQuery';

export interface AgencyProfilesState {
  agencies: Agency[];
  agenciesLoadState: LoadState;
  totalAgenciesCount: number;
  agencyListCurrentPage: number;
  selectedLocations: GeoLocation[];
  /** `null` instead of `undefined` so the key survives SSR state serialization and stays reactive after hydration. */
  searchedLocations: GeoLocation[] | null;
  /** True after leaving the page until the next route sync confirms or replaces `searchedLocations`. */
  isSearchedLocationsStale: boolean;
  agenciesCountLoadState: LoadState;
  totalFilteredAgenciesCount: number;
  includeChildrenAgencies: boolean;
  selectedListingType: ListingType;
  selectedSortingOption: SortingOption;
  /** Filters used for the currently displayed agency list (may lag UI selection until refetch). */
  loadedListingType: ListingType;
  loadedSortingOption: SortingOption;
  loadedIncludeChildrenAgencies: boolean;
  agenciesRequestGeneration: number;
  /** Agency-only query keys preserved for agent ↔ agency tab switches. */
  lastAgencyDirectoryQuery: AgencyDirectoryStashQuery;
}

const buildSearchedLocationsPatch = (
  searchedLocations: GeoLocation[] | null,
  resolvedLocations: GeoLocation[],
  currentPage: number,
): Partial<AgencyProfilesState> => {
  const patch: Partial<AgencyProfilesState> = {
    isSearchedLocationsStale: false,
    agencyListCurrentPage: currentPage,
  };

  if (searchedLocations === null || !haveSameSearchedGeoIds(searchedLocations, resolvedLocations)) {
    patch.searchedLocations = resolvedLocations;
  }

  return patch;
};

export const useAgencyProfilesStore = defineStore('agencyProfiles', {
  state: (): AgencyProfilesState => ({
    agencies: [],
    agenciesLoadState: LoadState.UNDEFINED,
    totalAgenciesCount: 0,
    agencyListCurrentPage: 1,
    /* selectedLocations represent location autocomplete input state */
    selectedLocations: [],
    /* searchedLocations represent locations user actively searched for (clicked search button) */
    searchedLocations: null,
    isSearchedLocationsStale: false,
    agenciesCountLoadState: LoadState.UNDEFINED,
    totalFilteredAgenciesCount: 0,
    includeChildrenAgencies: false,
    selectedListingType: ListingType.ANY,
    selectedSortingOption: SortingOption.DEFAULT,
    loadedListingType: ListingType.ANY,
    loadedSortingOption: SortingOption.DEFAULT,
    loadedIncludeChildrenAgencies: false,
    agenciesRequestGeneration: 0,
    lastAgencyDirectoryQuery: {},
  }),
  actions: {
    /**
     * Loads the agencies for the current page.
     * @param currentPage - The current page number (1-based index)
     */
    async loadAgencies(currentPage = 1, requestGeneration?: number) {
      const generation = requestGeneration ?? ++this.agenciesRequestGeneration;

      this.agencies = [];
      this.agenciesLoadState = LoadState.LOADING;
      const geoLocationIds = this.selectedLocations
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agencies = await fetchAgencies({
          from: (currentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE,
          geoLocationIds: geoLocationIds,
          listingType: this.selectedListingType,
          includeChildren: this.includeChildrenAgencies,
          sortBy: this.selectedSortingOption,
        });

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        this.agencies = agencies.records;
        this.totalAgenciesCount = agencies.total;
        this.totalFilteredAgenciesCount = agencies.total;
        this.agenciesLoadState = LoadState.LOADED;
      } catch (error) {
        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        this.agenciesLoadState = LoadState.FAILED;
      }
    },
    async loadAgenciesCount() {
      this.agenciesCountLoadState = LoadState.LOADING;
      const geoLocationIds = this.selectedLocations
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agencies = await fetchAgencies({
          size: 0,
          geoLocationIds: geoLocationIds,
          listingType: this.selectedListingType,
          includeChildren: this.includeChildrenAgencies,
        });
        this.totalFilteredAgenciesCount = agencies.total;
        this.agenciesCountLoadState = LoadState.LOADED;
      } catch (error) {
        this.agenciesCountLoadState = LoadState.FAILED;
      }
    },
    changeSelectedLocations(geoLocations: GeoLocation[]) {
      this.selectedLocations = geoLocations;
    },
    setRouteResolvedLocations(resolvedLocations: GeoLocation[]) {
      // searchedLocations is kept as-is: a new reference recomputes searchedGeoIds and
      // makes FeaturedPlacement refetch, which can return a different featured agency.
      this.selectedLocations = resolvedLocations;
      this.isSearchedLocationsStale = false;
    },
    syncLastAgencyDirectoryQueryFromRoute(query: LocationQuery) {
      this.lastAgencyDirectoryQuery = pickDirectoryStashQueryFromRoute(
        query,
        AGENCY_DIRECTORY_STASH_QUERY_KEYS,
      );
    },
    /**
     * Keeps the loaded search for a later visit, but holds back FeaturedPlacement until the next
     * route sync, so it does not mount with geo ids that are about to change.
     */
    markSearchedLocationsStale() {
      this.agenciesRequestGeneration++;
      this.isSearchedLocationsStale = true;
    },
    beginAgencyRouteSync(): number {
      return ++this.agenciesRequestGeneration;
    },
    async changeUrlParams({
      geoLocations,
      locale,
      includeChildren,
      currentPage,
      listingType,
      sortBy,
      requestGeneration,
      resolvedLocations: resolvedLocationsParam,
    }: {
      geoLocations: string[];
      locale: HgSupportedLanguage;
      includeChildren: boolean;
      currentPage: number;
      listingType?: ListingType;
      sortBy?: SortingOption;
      requestGeneration?: number;
      resolvedLocations?: GeoLocation[];
    }) {
      const generation = requestGeneration ?? ++this.agenciesRequestGeneration;

      try {
        const resolvedLocations =
          resolvedLocationsParam ?? (await resolveGeoLocations(geoLocations, locale));

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        // The route is the source of truth: an empty location param means Switzerland-wide search.
        this.$patch({
          selectedLocations: resolvedLocations,
        });

        this.includeChildrenAgencies = includeChildren;

        if (listingType) {
          this.selectedListingType = listingType;
        }

        if (sortBy) {
          this.selectedSortingOption = getAgencySortByOption(sortBy, this.selectedListingType);
        }

        this.$patch(
          buildSearchedLocationsPatch(this.searchedLocations, resolvedLocations, currentPage),
        );

        await this.loadAgencies(currentPage, generation);

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        this.$patch({
          loadedListingType: this.selectedListingType,
          loadedSortingOption: this.selectedSortingOption,
          loadedIncludeChildrenAgencies: this.includeChildrenAgencies,
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  getters: {
    pageCount: (state): number =>
      Math.ceil(state.totalAgenciesCount / DEFAULT_AGENCY_PAGE_LIST_SIZE),
    nextPageRoute: (state) => {
      return (page: number, locale: Locale) => {
        const nextRoute: {
          params: { location: string | undefined };
          query: {
            page?: number;
            loc?: string;
            includeChildren?: boolean;
            listingType?: ListingType;
            sortBy?: SortingOption;
          };
        } = {
          params: { location: undefined },
          query: {
            ...(page > 1 ? { page } : {}),
            ...(state.includeChildrenAgencies
              ? { includeChildren: state.includeChildrenAgencies }
              : {}),
            ...(state.selectedListingType !== ListingType.ANY
              ? { listingType: state.selectedListingType }
              : {}),
            ...(getAgencySortByOption(state.selectedSortingOption, state.selectedListingType) !==
            SortingOption.DEFAULT
              ? { sortBy: state.selectedSortingOption }
              : {}),
          },
        };
        return applySelectedLocationsToRoute(nextRoute, state.selectedLocations, locale);
      };
    },
    searchedGeoIds: (state) =>
      state.searchedLocations
        ?.map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id),
  },
});
